import React, { useState, useEffect, useMemo, useRef } from "react"
import { Link } from "react-router-dom"

import getAppConfig from "~/utils/getAppConfig"
import { apiLineItems } from "~/routes"
import { formatCurrency } from "~/utils/helpers"

import CatchCard from "~/library/CatchCard"
import CatchTable from "~/library/CatchTable"
import CatchModal from "~/library/CatchModal"
import LoadingWrapper from "~/library/LoadingWrapper"
import Pagination from "~/library/Pagination"
import useFetchResource from "~/hooks/useFetchResource"
import { useToast } from "~/utils/Toast"
import NavigationLink from "~/library/NavigationLink"
import LineItem from "~/types/LineItem"
import BookingItem from "~/types/BookingItem"

interface LineItemModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (changes: BookingItemChange[]) => void
  existingBookingItems: BookingItem[]
}

interface BookingItemChange {
  id?: string // For existing items
  lineItemId: string
  name: string
  categoryName?: string
  quantity: number
  amountCents: number
  amountCurrency: string
  action: "add" | "update" | "remove"
}

const LineItemModal: React.FC<LineItemModalProps> = ({
  isOpen,
  onClose,
  onSave,
  existingBookingItems,
}) => {
  const { venueSlug } = getAppConfig()
  const [selectedQuantities, setSelectedQuantities] = useState<{ [key: string]: number }>({})
  const [pendingChanges, setPendingChanges] = useState<{ [key: string]: BookingItemChange }>({})
  const initializedRef = useRef(false)
  const toast = useToast()

  const scopeParams = {
    "q[active_eq]": "true",
  }

  const {
    data: availableLineItems,
    refetch,
    metadata,
    isLoading: isLineItemsLoading,
  } = useFetchResource<{ lineItems: LineItem[] }, "lineItems">(
    apiLineItems.index.path({ venueSlug }),
    "lineItems",
    { page: "1", ...scopeParams }
  )

  const handlePageChange = (page: string) => {
    refetch({ page: page, ...scopeParams })
  }

  // Memoize the initial quantities calculation to prevent unnecessary recalculations
  const initialQuantities = useMemo(() => {
    const quantities: { [key: string]: number } = {}

    // Set quantities for existing booking items
    existingBookingItems.forEach((item) => {
      quantities[item.lineItemId] = item.quantity
    })

    return quantities
  }, [
    existingBookingItems.length,
    JSON.stringify(
      existingBookingItems.map((item) => ({ id: item.lineItemId, qty: item.quantity }))
    ),
  ])

  // Initialize quantities when modal opens
  useEffect(() => {
    if (isOpen && !initializedRef.current) {
      setSelectedQuantities(initialQuantities)
      initializedRef.current = true
    } else if (!isOpen) {
      initializedRef.current = false
      setPendingChanges({}) // Clear pending changes when modal closes
    }
  }, [isOpen, initialQuantities])

  const handleQuantityChange = (lineItemData: any, quantity: string) => {
    const lineItemId = lineItemData.uuid
    const qty = parseInt(quantity) || 1
    setSelectedQuantities((prev) => ({
      ...prev,
      [lineItemId]: qty,
    }))

    // Update pending changes if this item is already selected for changes
    const existingChange = pendingChanges[lineItemId]
    if (existingChange) {
      setPendingChanges((prev) => ({
        ...prev,
        [lineItemId]: {
          ...existingChange,
          quantity: qty,
        },
      }))
    }

    if (qty < lineItemData.minOrderQty) {
      toast(
        `${lineItemData.name} quantity was set below the minimum order quantity of ${lineItemData.minOrderQty}`,
        "warning"
      )
    }
  }

  const handleAddItem = (lineItem: any) => {
    const lineItemId = lineItem.uuid
    const quantity = selectedQuantities[lineItemId] || 1
    const changeKey = lineItemId
    const existingBookingItem = existingBookingItems.find((item) => item.lineItemId === lineItemId)

    // Add to pending changes
    setPendingChanges((prev) => ({
      ...prev,
      [changeKey]: {
        ...(existingBookingItem && { id: existingBookingItem.id }), // Include ID for existing items
        lineItemId: lineItemId,
        name: lineItem.name,
        categoryName: lineItem.categoryName,
        quantity: quantity,
        amountCents: lineItem.amountCents,
        amountCurrency: lineItem.amountCurrency || "USD",
        action: existingBookingItem ? "update" : "add",
      },
    }))
  }

  const handleSave = () => {
    const changes = Object.values(pendingChanges)
    onSave(changes)
    setPendingChanges({})
    onClose()
  }

  const isItemInBooking = (lineItemData: any) => {
    const lineItemId = lineItemData.uuid
    return existingBookingItems.some((item) => item.lineItemId === lineItemId)
  }

  const hasChanges = Object.keys(pendingChanges).length > 0

  return (
    <CatchModal
      isOpen={isOpen}
      onClose={onClose}
      title="Manage Line Items"
      size="xl"
      footer={
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!hasChanges}
          >
            Save Changes {hasChanges && `(${Object.keys(pendingChanges).length})`}
          </button>
        </div>
      }
    >
      <LoadingWrapper isLoading={isLineItemsLoading}>
        {Array.isArray(availableLineItems) && availableLineItems.length === 0 ? (
          <div className="text-center p-3">
            <p>No line items available.</p>
            <Link to="/line-items/new" className="btn btn-primary">
              Create Line Item
            </Link>
          </div>
        ) : (
          <CatchCard>
            <div className="mb-3">
              <small className="text-muted">
                Select line items to add to this booking. Change quantities and click
                &#34;Update&#34; for existing items.
              </small>
              {hasChanges && (
                <div className="alert alert-info mt-2 mb-0">
                  <small>
                    <strong>Selected:</strong> {Object.keys(pendingChanges).length} items will be
                    added
                  </small>
                </div>
              )}
            </div>
            <CatchTable
              data={Array.isArray(availableLineItems) ? availableLineItems : []}
              columns={[
                {
                  key: "name",
                  header: "Name",
                  formatter: (name, data) => (
                    <NavigationLink type="line-items" to={`/line-items/${data.id}`}>
                      {name}
                    </NavigationLink>
                  ),
                },
                {
                  key: "categoryName",
                  header: "Category",
                  formatter: (category: string) => category || "—",
                },
                {
                  key: "amountCents",
                  header: "Unit Price",
                  formatter: (amountCents: number) =>
                    formatCurrency({ cents: amountCents, currency_iso: "USD" }),
                },
                {
                  key: "minOrderQty",
                  header: "Min Order Qty",
                  formatter: (minOrderQty: number) => minOrderQty || "—",
                },
                {
                  key: "quantity",
                  header: "Quantity",
                  formatter: (_, data: any) => (
                    <input
                      type="number"
                      min={data.minOrderQty || 1}
                      value={selectedQuantities[data.uuid] || data.minOrderQty || 1}
                      onChange={(e) => handleQuantityChange(data, e.target.value)}
                      className="form-control"
                      style={{ width: "80px" }}
                    />
                  ),
                },
                {
                  key: "subtotal",
                  header: "Subtotal",
                  formatter: (_, data: any) => {
                    const quantity = selectedQuantities[data.uuid] || 1
                    const subtotal = data.amountCents * quantity
                    return formatCurrency({ cents: subtotal, currency_iso: "USD" })
                  },
                },
                {
                  key: "actions",
                  header: "Actions",
                  formatter: (_, data: any) => {
                    const inBooking = isItemInBooking(data)
                    const pendingChange = pendingChanges[data.uuid]
                    const existingBookingItem = existingBookingItems.find(
                      (item) => item.lineItemId === data.uuid
                    )
                    const hasQuantityChange =
                      existingBookingItem &&
                      selectedQuantities[data.uuid] !== existingBookingItem.quantity

                    // Show update button if quantity changed for existing items
                    if (inBooking && hasQuantityChange && !pendingChange) {
                      return (
                        <button
                          type="button"
                          className="btn btn-warning btn-sm"
                          onClick={() => handleAddItem(data)}
                        >
                          Update
                        </button>
                      )
                    }

                    return (
                      <button
                        type="button"
                        className="btn btn-success btn-sm"
                        onClick={() => handleAddItem(data)}
                        disabled={(inBooking && !hasQuantityChange) || !!pendingChange}
                      >
                        {pendingChange ? (
                          <span>&#x2713;</span>
                        ) : inBooking ? (
                          <span>&#x2713;</span>
                        ) : (
                          <span>+</span>
                        )}
                      </button>
                    )
                  },
                },
              ]}
            />
            {metadata && (
              <Pagination
                metadata={metadata}
                onPageChange={(page: number) => handlePageChange(page.toString())}
              />
            )}
          </CatchCard>
        )}
      </LoadingWrapper>
    </CatchModal>
  )
}

export default LineItemModal
